import React, { useCallback, useState, useRef } from 'react';
import { Tooltip, Icon, message, Switch, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import DocumentName from '../../components/DocumentName';
import EmailAutoSaveButton from './EmailAutoSaveButton';
import {
  useDocumentContext,
  useComplexDocumentHistory,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import { useEmailContext, COMPLEX_EMAIL_CONTEXT_KEYS } from '../../context/complexEmailContext';
import { ActionButton, UndoIcon, RedoIcon, EndContainer, PreviewEndContainer } from '../ComplexDocumentDesign/styles';
import { TemplateName } from '../SidebarMenu/styles';
import { ElementBackgroundColor, VersionSection } from '../../artboard/Toolbar/styles';
import { TEMPLATES_DESIGN_AREA_TOOLBAR } from '../../artboard/Toolbar/translationValues';
import { COMPLEX_DESIGN_TOOLBAR, COMPLEX_DOCUMENT_ENVIRONMENT } from '../ComplexDocumentDesign/translationValues';
import { COMPLEX_EMAIL_TOOLBAR } from './translationValues';
import {
  lockResource,
  unlockResource,
  checkLockStatus,
  updateCommunicationName
} from '../../utils/services/complexDocumentsAPI';
import { COMPLEX_DOCUMENT_VERSION, COMPLEX_DOCUMENT_PAGE_WRAPPER } from '../../utils/complexDocumentConstants';
import ComplexEmailVersion from './ComplexEmailVersion';
import { useSessionContext } from '../../context/sessionContext';
import { ELEMENT_POSITION_TOOLBAR_ID } from '../../artboard/Toolbar/elementPositionToolbar';
import { emailSendToTesting, updateEmailResourceNames } from '../../utils/services/complexEmailAPI';
import { TEMPLATE_NAME_CHAR_LIMIT } from '../../utils/constants/appConstants';
import { testingIcon, versionIcon } from '../../images/svgIcons';
import { checkUserAccessRoles } from '../../utils/helper';

const NAMESPACES = [
  'TEMPLATES_DESIGN_AREA_TOOLBAR',
  'COMPLEX_DESIGN_TOOLBAR',
  'COMPLEX_DOCUMENT_ENVIRONMENT',
  'COMPLEX_EMAIL_TOOLBAR',
  'COMPLEX_EMAIL_PREVIEW_SETTINGS'
];

const EmailTemplateName = ({ editable }) => {
  const { t } = useTranslation(NAMESPACES);
  const containerRef = useRef(null);
  const pageWrapperTop = useRef(null);
  const customSelector = useCallback(
    store => ({
      resourceName: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME],
      resourceId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
      communicationId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID]
    }),
    [],
  );
  const [docInfo, store] = useDocumentContext(customSelector, `EmailTemplateName`);
  const [showOptions, toggleOptions] = useState(false);
  const [isEnvloading, setIsEnvloading] = useState(false);
  const [versionView, toggleVersionView] = useState(false);
  const [blur, setBlur] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [{ undoStack, redoStack }] = useComplexDocumentHistory(store => ({
    undoStack: store.undoStack,
    redoStack: store.redoStack,
  }));

  const lifecycleSelector = useCallback(store => store[CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle] = useArtboardContext(lifecycleSelector, `EmailTemplateName`);

  const templateLockedSelector = useCallback(store => store[CONTEXT_KEYS.TEMPLATE_LOCKED], []);
  const [isTemplateLocked] = useArtboardContext(templateLockedSelector, `EmailTemplateName`);

  const lockedUserIdSelector = useCallback(store => store[CONTEXT_KEYS.LOCKED_USER_ID], []);
  const [lockedUserID] = useArtboardContext(lockedUserIdSelector, `EmailTemplateName`);

  const currentBlockSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK], []);
  const [currentBlock] = useArtboardContext(currentBlockSelector, `EmailTemplateName`);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign, updateArtboard] = useArtboardContext(
    currentDesignSelector,
    `EmailTemplateName`,
  );

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const previewSelector = useCallback(store => store[CONTEXT_KEYS.IS_PREVIEW], []);
  const [isPreview] = useArtboardContext(previewSelector, `EmailTemplateName`);

  const sendingEmailSelector = useCallback(store => store[CONTEXT_KEYS.GENERATING_PDF], []);
  const [sendingEmail] = useArtboardContext(sendingEmailSelector, `ComplexDocumentName`);

  const previewDownloadSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_AND_DOWNLOAD], []);
  const [isPreviewDownload] = useArtboardContext(previewDownloadSelector, `EmailTemplateName`);

  const mobileViewSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.IS_MOBILE_VIEW], []);
  const [, emailMethods] = useEmailContext(mobileViewSelector);

  const blocksNotFit = document.getElementsByClassName('block-not-fit');
  const isDocumentHasError = blocksNotFit.length > 0;

  const enableVersionView = useCallback(enabled => {
    toggleVersionView(enabled);
    if (enabled) {
      containerRef.current.parentNode.style['z-index'] = 11;
    } else {
      containerRef.current.parentNode.style['z-index'] = 2;
    }
  }, []);

  const undoNameChange = useCallback(() => {
    setBlur(false);
    const blurTimeOut = setTimeout(() => {
      setBlur(true);
      clearTimeout(blurTimeOut);
    }, 100);
  }, []);

  const changeTemplateName = async input => {
    // Validations: The template name must not be empty, and it should differ from the previous one
    // If the input value is empty, restore the previous template name
    const name = input.trim();
    if(name.length > TEMPLATE_NAME_CHAR_LIMIT) {
      message.error(t(COMPLEX_DESIGN_TOOLBAR.MAX_CHAR_LIMIT_EXCEEDED_ERROR.key, COMPLEX_DESIGN_TOOLBAR.MAX_CHAR_LIMIT_EXCEEDED_ERROR.value, { charLimitLength: TEMPLATE_NAME_CHAR_LIMIT }));
      return;
    }
    if (name.length < 1) {
      undoNameChange();
      message.warn(
        t(
          COMPLEX_DESIGN_TOOLBAR.TEMPLATE_NAME_EMPTY_WARNING_MESSAGE.key,
          COMPLEX_DESIGN_TOOLBAR.TEMPLATE_NAME_EMPTY_WARNING_MESSAGE.value,
        ),
      );
      return;
    }

    if (name !== docInfo.resourceName) {
      let payload = { name };
      if (currentDesign === 'document') {
        payload.templateId = docInfo.resourceId;
      } else if (currentDesign === 'block') {
        payload.blockId = docInfo.resourceId;
      }
      let response;
      if (currentDesign === 'document') {
        response = await updateCommunicationName(docInfo.communicationId, name);
      } else {
        response = await updateEmailResourceNames(payload, currentDesign !== 'document');
      }
      if (response?.statusCode === 1804 || response?.statusCode === 1927 || response.statusCode === 3201) {
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME]: name });
      } else {
        if(response?.statusCode === 1941 || response?.statusCode === 2616) {
          undoNameChange();
        } else {
          if(response?.statusCode === 3200 && response?.errors?.some((error => error?.code === 3003))) {
            message.error(t(
              COMPLEX_DESIGN_TOOLBAR.COMMUNICATION_NAME_EXIST_ERROR.key,
              COMPLEX_DESIGN_TOOLBAR.COMMUNICATION_NAME_EXIST_ERROR.value
            ));
          } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_ERROR_MESSAGE.key,
              COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_ERROR_MESSAGE.value,
            ));
          }
        }
      }
    }
  };

  const sendToTesting = useCallback(async () => {
    if (isDocumentHasError) {
      return message.error(
        t(
          COMPLEX_DESIGN_TOOLBAR.ERROR_IN_DOCUMENT.key,
          COMPLEX_DESIGN_TOOLBAR.ERROR_IN_DOCUMENT.value,
        ),
        6,
      );
    }
    setIsEnvloading(true);
    const response = await emailSendToTesting(docInfo.resourceId, currentDesign);
    const statusCode = [2601, 728, 1904];
    if (statusCode.includes(response?.statusCode)) {
      // TODO : Handle for flows / blocks
      message.success(
        t(
          COMPLEX_DOCUMENT_ENVIRONMENT.MOVED_TO_TESTING_SUCCESSFULLY_MESSAGE.key,
          COMPLEX_DOCUMENT_ENVIRONMENT.MOVED_TO_TESTING_SUCCESSFULLY_MESSAGE.value,
        ),
      );
      // TODO : Only on success
      updateArtboard({
        [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
          [COMPLEX_DOCUMENT_VERSION.QA_READY]: new Date(),
        }),
      });
    } else {
      message.error(
        t(
          COMPLEX_DOCUMENT_ENVIRONMENT.DEFAULT_ERROR_MESSAGE.key,
          COMPLEX_DOCUMENT_ENVIRONMENT.DEFAULT_ERROR_MESSAGE.value,
        ),
      );
    }
    setIsEnvloading(false);
  }, [docInfo, currentDesign, lifeCycle]);

  const onVersionViewClose = useCallback(() => {
    enableVersionView(false);
  }, []);

  const showSendToTesting = lifeCycle[COMPLEX_DOCUMENT_VERSION.DRAFT]
    ? lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY]
      ? new Date(lifeCycle[COMPLEX_DOCUMENT_VERSION.DRAFT]) >
        lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY]
      : true
    : false;

  const handleReadModeChange = async e => {
    let payload = {
      resourceId: docInfo.resourceId,
      resourceType: currentDesign === 'document' ? 'email' : currentDesign === 'block' ? 'email_block' : undefined
    };
    setIsLoading(true);
    try {
      const lockStatus = await checkLockStatus(payload);
      if (lockStatus.statusCode === 741) {
        if (userDetails.id !== lockStatus.data.userId) {
          updateArtboard({
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
            [CONTEXT_KEYS.LOCKED_USER_ID]: lockStatus.data.userId,
          });
          return;
        }
      }
      if (!isTemplateLocked) {
        const response = await unlockResource(payload);
        if (response.statusCode === 721) {
          updateArtboard({
            [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
            [CONTEXT_KEYS.CURRENT_BLOCK]: '',
            [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false,
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
            [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
          });
        } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_READ_MODE.key,
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_READ_MODE.value,
            ),
          );
        }
      } else {
        const response = await lockResource(payload);
        if (response.statusCode === 719) {
          updateArtboard({
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: false,
            [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: true,
          });
        } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_EDITABLE_MODE.key,
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_EDITABLE_MODE.value,
            ),
          );
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewDownload = () => {
    updateArtboard({ [CONTEXT_KEYS.PREVIEW_AND_DOWNLOAD]: !isPreviewDownload });
    const complexPageWrapper = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
    if (!isPreviewDownload === false) {
      if (pageWrapperTop.current !== null) {
        complexPageWrapper.scrollTop = pageWrapperTop.current;
        pageWrapperTop.current = null;
      }
      emailMethods.set({
        [COMPLEX_EMAIL_CONTEXT_KEYS.IS_MOBILE_VIEW]: false
      });
    } else {
      pageWrapperTop.current = complexPageWrapper.scrollTop;
    }
  };

  return (
    <>
      <TemplateName ref={containerRef} previewEnabled={isPreview}>
        {isPreview && <div class="blur-overlay"></div>}
        <div>
          <DocumentName
            disabled={!editable}
            templateName={docInfo.resourceName}
            changeTemplateName={changeTemplateName}
            onBlur={blur}
            isElement={false}
          />
          {editable && (
            <>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.UNDO_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.UNDO_TOOLTIP.value,
                )}
              >
                <ActionButton
                  id="undoBtn"
                  disabled={!editable && undoStack.length === 0}
                  onClick={store.undo}
                >
                  <UndoIcon color={undoStack.length === 0 ? '#6a7580' : '#fff'} />
                </ActionButton>
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.REDO_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.REDO_TOOLTIP.value,
                )}
              >
                <ActionButton
                  id="redoBtn"
                  disabled={!editable && redoStack.length === 0}
                  onClick={store.redo}
                >
                  <RedoIcon color={redoStack.length === 0 ? '#6a7580' : '#fff'} />
                </ActionButton>
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.AUTO_SAVE_CHANGES_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.AUTO_SAVE_CHANGES_TOOLTIP.value,
                )}
              >
                <EmailAutoSaveButton editable={editable} noInternetTooltip={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.NO_INTERNET_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.NO_INTERNET_TOOLTIP.value,
                 )} />
              </Tooltip>
            </>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            width: '100%',
          }}
        >
          <div
            id="document-editor-toolbar"
            style={{
              margin: 5,
              height: 30,
              position: 'relative',
              color: 'black',
              display: 'flex',
              alignItems: 'center',
            }}
          />
          <div id={ELEMENT_POSITION_TOOLBAR_ID} />
        </div>
        <PreviewEndContainer isPreview={isPreview}>
          {!currentBlock && (
            <>
            <div id="email-preview-options" />
            <Button ghost onClick={handlePreviewDownload} loading={sendingEmail}  className={isPreview && 'exit_preview'}>
              {isPreview
                ? checkUserAccessRoles(userDetails, ['DESIGNER', 'PUBLISHER']) && currentDesign === 'document' ? t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_AND_SEND_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_AND_SEND_BUTTON.value,
                  ) : t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.EXIT_PREVIEW_BUTTON.value,
                  )
                : sendingEmail
                  ? 'Sending Emails...'
                  : checkUserAccessRoles(userDetails, ['DESIGNER', 'PUBLISHER']) && currentDesign === 'document' ? t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.PREVIEW_AND_SEND_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.PREVIEW_AND_SEND_BUTTON.value,
                  ) : t(
                    `COMPLEX_EMAIL_TOOLBAR:${COMPLEX_EMAIL_TOOLBAR.PREVIEW_BUTTON.key}`,
                    COMPLEX_EMAIL_TOOLBAR.PREVIEW_BUTTON.value,
                  )}
            </Button>
            </>
          )}
        </PreviewEndContainer>
        <EndContainer currentBlock={currentBlock} isPreview={isPreview}>
          <div className="small-switch switch2">
            <Switch
              checked={isTemplateLocked}
              disabled={lockedUserID !== userDetails.id}
              onChange={e => handleReadModeChange(e)}
              loading={isLoading}
              size="small"
            />
            <span className="switch-name">
              {t(
                COMPLEX_DESIGN_TOOLBAR.READ_MODE_LABEL.key,
                COMPLEX_DESIGN_TOOLBAR.READ_MODE_LABEL.value,
              )}
            </span>
          </div>
          <VersionSection className={showOptions ? 'show' : ''} show={showOptions}>
            {showOptions ? (
              <>
                {' '}
                <div className={'option'}>
                  <div
                    className={'option versionSvg'}
                    onClick={() => {
                      toggleOptions(!showOptions);
                    }}
                  >
                    <Tooltip
                      placement="left"
                      title={t(
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_VIEW_TEST_AND_PUBLISH_TOOLTIP.key,
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_VIEW_TEST_AND_PUBLISH_TOOLTIP.value,
                      )}
                    >
                      <Icon
                        component={versionIcon}
                        className="version"
                        onClick={() => {
                          enableVersionView(true);
                        }}
                      />
                    </Tooltip>
                    <p>
                      {t(
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_ENV_TOOLTIP.key,
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_ENV_TOOLTIP.value,
                      )}
                    </p>
                  </div>
                </div>
                {editable && showSendToTesting && (
                  <div className={'option testingSvg'}>
                    {isEnvloading ? (
                      <Icon type="loading" />
                    ) : (
                      <Tooltip
                        placement="left"
                        title={t(
                          TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_SEND_FOR_TESTING_TOOLTIP.key,
                          TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_SEND_FOR_TESTING_TOOLTIP.value,
                        )}
                      >
                        {' '}
                        <Icon
                          className="testIcon"
                          component={testingIcon}
                          onClick={sendToTesting}
                        />
                      </Tooltip>
                    )}
                    <p className="test">
                      {' '}
                      {t(
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_SEND_FOR_TESTING_TOOLTIP.key,
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_SEND_FOR_TESTING_TOOLTIP.value,
                      )}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {' '}
                <div className={'option'}>
                  <div className={'option versionSvg'}>
                    <Icon component={versionIcon} className="version" />
                    <p>
                      {t(
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_ENV_TOOLTIP.key,
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_ENV_TOOLTIP.value,
                      )}
                    </p>
                  </div>
                </div>
                {editable && showSendToTesting && (
                  <div className={'option testingSvg'}>
                    {isEnvloading ? (
                      <Icon type="loading" />
                    ) : (
                      <Tooltip placement="left" title="">
                        {' '}
                        <Icon className="testIcon" component={testingIcon} />
                      </Tooltip>
                    )}
                    <p className="test">
                      {t(
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_SEND_FOR_TESTING_TOOLTIP.key,
                        TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_SEND_FOR_TESTING_TOOLTIP.value,
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </VersionSection>
          <Tooltip
            placement="bottom"
            title={t(
              TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_TOOLTIP.key,
              TEMPLATES_DESIGN_AREA_TOOLBAR.VERSION_TOOLTIP.value,
            )}
          >
            <ElementBackgroundColor
              onClick={() => {
                toggleOptions(!showOptions);
              }}
              version={1}
              style={{
                marginRight: '15px',
                marginLeft: '0px',
              }}
            >
              <svg
                id="Layer_1"
                style={{
                  enableBackground: 'new 0 0 1024 1024',
                }}
                viewBox="0 0 1024 1024"
                xmlSpace="preserve"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
              >
                <g id="XMLID_39_">
                  <path
                    d="M914.3,807v40H300.5c-9.4,43.4-48.1,76-94.3,76c-53.2,0-96.5-43.3-96.5-96.5c0-53.2,43.3-96.5,96.5-96.5   c46.5,0,85.5,33.1,94.5,77H914.3z"
                    id="XMLID_375_"
                  />
                  <path
                    d="M914.3,492v40H772.4c-9.2,43.6-48.1,76.5-94.4,76.5c-46.3,0-85.2-32.8-94.4-76.5H124.3v-40h459.3   c9.2-43.6,48.1-76.5,94.4-76.5c46.3,0,85.2,32.8,94.4,76.5H914.3z"
                    id="XMLID_385_"
                  />
                  <path
                    d="M914.3,177v40h-442c-9,43.9-48,77-94.5,77c-46.5,0-85.4-33.1-94.5-77h-159v-40h159.2   c9.4-43.4,48.1-76,94.3-76c46.2,0,84.9,32.6,94.3,76H914.3z"
                    id="XMLID_392_"
                  />
                </g>
                <g id="XMLID_1_" />
                <g id="XMLID_2_" />
                <g id="XMLID_3_" />
                <g id="XMLID_4_" />
                <g id="XMLID_5_" />
              </svg>
              {showOptions ? <Icon type="up" /> : <Icon type="down" />}
            </ElementBackgroundColor>
          </Tooltip>
        </EndContainer>
      </TemplateName>
      {versionView && (
        <ComplexEmailVersion
          documentId={docInfo.resourceId}
          onClose={onVersionViewClose}
          editable={editable}
          currentDesign={currentDesign}
        />
      )}
    </>
  );
};

export default EmailTemplateName;